import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { fetchAll } from '@/services/general/fetchAll'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'
import { getLast31DaysPeriod } from '@/utils/get-last-31-days-period'

interface IGeneralProps {
  currentFilters: Filters | undefined
  handleFilter: (filtros: Filters) => Promise<void>
  handleLimitChange: (newLimit: number) => void
  handleSelectItem: (item: string[], action: boolean) => void
  isLoading: boolean
  limit: number
  numbers: NumbersSummary
  page: number
  results: number
  search: string
  selectItems: any[]
  setCurrentFilters: React.Dispatch<React.SetStateAction<Filters | undefined>>
  setLimit: React.Dispatch<React.SetStateAction<number>>
  setNumbers: React.Dispatch<React.SetStateAction<NumbersSummary>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSelectItems: React.Dispatch<React.SetStateAction<any[]>>
  setTableData: React.Dispatch<React.SetStateAction<any[]>>
  tableData: any[]
}

type Filters = {
  dataDoItem: any
  companyId: string
  contract: string
}

type NumbersSummary = {
  total: number
  integrados: number
  erro: number
}

const GeneralContext = createContext<IGeneralProps | undefined>(undefined)

const GeneralProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [tableData, setTableData] = useState<any[]>([])
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [currentFilters, setCurrentFilters] = useState<Filters | undefined>(
    undefined
  )
  const [results, setResults] = useState<number>(0)
  const [numbers, setNumbers] = useState<NumbersSummary>({
    total: 0,
    integrados: 0,
    erro: 0
  })
  const [selectItems, setSelectItems] = useState([])

  const dates = getLast31DaysPeriod().split(' - ')
  const defaultStartDate = formatBrDateToIso(dates[0])
  const defaultEndDate = formatBrDateToIso(dates[1])
  const period = `${defaultStartDate} - ${defaultEndDate}`

  useEffect(() => {
    const defaultFilters = {
      dataDoItem: period,
      companyId: null,
      contract: null
    }

    setCurrentFilters(defaultFilters)
  }, [])

  const fetchData = async (filtros: Filters) => {
    setIsLoading(true)

    let startDate = ''
    let endDate = ''

    if (filtros.dataDoItem) {
      const dates = filtros.dataDoItem.split(' - ')
      startDate = new Date(dates[0]).toISOString().split('T')[0]
      endDate = new Date(dates[1]).toISOString().split('T')[0]
    }

    const companyIdValue = filtros.companyId
    const contractValue = filtros.contract

    const payload = {
      DataInicio: startDate,
      DataFim: endDate,
      Cnpj: companyIdValue,
      IdContrato: contractValue,
      offset: page,
      limit
    }

    const allData = await fetchAll(payload)
    setTableData(allData.items)
    setResults(allData.total)
    setNumbers(allData.numbers)
    setIsLoading(false)
  }

  const handleFilter = async (filtros: Filters) => {
    setCurrentFilters(filtros)
    setPage(1)
    await fetchData(filtros)
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1)
  }

  useEffect(() => {
    if (currentFilters) {
      fetchData(currentFilters)
    }
  }, [page, limit, search])

  const handleSelectItem = (item: string[], action: boolean) => {
    setSelectItems(prevItems => {
      if (action === false) {
        return prevItems.filter(i => i !== item)
      }
      return action ? [...prevItems, item] : prevItems
    })
  }

  const contextValue = useMemo(
    () => ({
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      numbers,
      page,
      results,
      search,
      selectItems,
      setCurrentFilters,
      setLimit,
      setNumbers,
      setPage,
      setSearch,
      setSelectItems,
      setTableData,
      tableData
    }),
    [
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      numbers,
      page,
      results,
      search,
      selectItems,
      setCurrentFilters,
      setLimit,
      setNumbers,
      setPage,
      setSearch,
      setSelectItems,
      setTableData,
      tableData
    ]
  )
  return (
    <GeneralContext.Provider value={contextValue}>
      {children}
    </GeneralContext.Provider>
  )
}

const useGeneralContext = () => {
  const context = useContext(GeneralContext)
  if (context === undefined) {
    throw new Error('useGeneralContext must be used within a GeneralProvider')
  }
  return context
}

export { GeneralProvider, useGeneralContext }
